<template>
  <div ref="widget" class="shop-by">
    <transition name="fade">
      <div v-if="loading" class="loader-wrapper"><div class="loader giant" /></div>
    </transition>

    <div ref="nav" class="shop-by-widget">
      <div class="head ctf-restrict">
        <h2 v-once class="shop-by-title light uppercase ctf-heading">{{ title }}</h2>
        <div v-if="showNavigation" class="shop-by-widget-nav">
          <transition name="fade">
            <div v-if="!loading" ref="navContainer" class="shop-by-nav">
              <div
                v-for="(room, index) in images"
                :key="index"
                :ref="setNavRef"
                :class="[{ active: activeRoomIndex === index }, getNavClassName(room.primaryRoom)]"
                @click="changeRoomDirectly(index)"
              >
                <span> {{ room.primaryRoom }} </span>
              </div>

              <div class="placeholder"><span>p</span></div>
            </div>
          </transition>

          <div v-if="loading" class="shop-by-nav">
            <div class="active"><span> Loading </span></div>
            <div><span> Rooms </span></div>
            <div><span> ... </span></div>
          </div>
        </div>
      </div>

      <div ref="galleryContainer" class="body ctf-indent">
        <ShopByWidgetGallery
          v-if="!loading && !loadingGallery"
          :is-mobile="isMobile"
          ref="gallery"
          class="gallery"
          :images="activeImages"
          :turn-off-all-experiments="turnOffAllExperiments"
          :room-name="getNavClassName(images[activeRoomIndex].primaryRoom)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import smoothReflow from '@/plugins/smoothReflow';
import { ref, onBeforeUpdate, nextTick } from 'vue';
import { MediaUtils } from '@/pixel/index';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage.js';
import ShopByWidgetGallery from './ShopByWidgetGallery.vue';

export default {
  name: 'ShopByWidget',

  components: {
    ShopByWidgetGallery,
  },

  mixins: [smoothReflow],

  props: {
    title: {
      type: String,
    },
    showNavigation: {
      default: true,
      type: Boolean,
    },
    browseByRoom: {
      default: '',
      type: String,
    },
    turnOffAllExperiments: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      images: [],
      activeImages: [],
      loading: true,
      loadingGallery: false,
      currentTemplateIndex: MediaUtils.isMobileAndSmaller,
      activeRoomIndex: 0,
      isMobile: MediaUtils.isMobileAndSmaller,
    };
  },

  setup() {
    const navLinkRefs = ref([]);
    const setNavRef = (el) => {
      navLinkRefs.value.push(el);
    };

    onBeforeUpdate(() => {
      navLinkRefs.value = [];
    });

    return {
      navLinkRefs,
      setNavRef,
    };
  },

  unmounted() {
    window.removeEventListener('resize', this.resized);
  },

  async mounted() {
    let roomIdeasEndpoint = 'roomideas_simplified_score';
    const url = this.browseByRoom ? `${roomIdeasEndpoint}?primaryRoom=${this.browseByRoom}` : `${roomIdeasEndpoint}`;

    this.$smoothReflow();

    window.addEventListener('resize', this.resized);

    const response = await fetch(`${import.meta.env.VITE_KRAKEN_MEDIA_API}/${url}`);
    const responseJson = await response.json();

    if (!responseJson?.data?.length) {
      this.$refs.widget.style.display = 'none';
      return;
    }

    if (this.browseByRoom) this.setUpBrowseRoom(responseJson);
    else this.setUpRooms(responseJson);

    if (window.hj) {
      window.hj('trigger', 'room_ideas');
    }

    LocalStorage.set(LocalStorageKeys.ROOM_IDEAS_WIDGET_STATE, 'uninteracted');
  },

  methods: {
    resized() {
      this.isMobile = MediaUtils.isMobileAndSmaller;
      if (MediaUtils.isMobileAndSmaller !== this.currentTemplateIndex) {
        this.currentTemplateIndex = MediaUtils.isMobileAndSmaller;
        this.changeRoom(this.activeRoomIndex);
      }
    },

    setActiveImages() {
      if (!this.images?.[this.activeRoomIndex]) {
        return [];
      }
      if (MediaUtils.isMobileAndSmaller) {
        this.activeImages = this.images[this.activeRoomIndex].templates[1].files;
      } else {
        this.activeImages = this.images[this.activeRoomIndex].templates[0].files;
      }
    },

    setUpBrowseRoom(data) {
      this.images = JSON.parse(JSON.stringify(data)).data;
      this.setActiveImages();
      this.loading = false;
      this.$emit('loaded', true);
    },

    setUpRooms(data) {
      const priority = ['Living Room', 'Dining Room', 'Bedroom', 'Outdoor', 'Home Office', 'Kitchen'];

      this.images = JSON.parse(JSON.stringify(data)).data.sort((a, b) => {
        if (a.primaryRoom === 'Outdoors') a.primaryRoom = 'Outdoor';
        if (b.primaryRoom === 'Outdoors') b.primaryRoom = 'Outdoor';
        if (priority.indexOf(a.primaryRoom) > priority.indexOf(b.primaryRoom)) {
          return 1;
        }
        return -1;
      });

      this.setActiveImages();
      this.loading = false;
    },

    getNavClassName(name) {
      return name.replace(/\s+/g, '-').toLowerCase();
    },

    changeToLastRoom() {
      if (this.activeRoomIndex === 0) {
        this.changeRoom(this.images.length - 1);
      } else {
        this.changeRoom(this.activeRoomIndex - 1);
      }
    },

    changeToNextRoom() {
      if (this.activeRoomIndex === this.images.length - 1) {
        this.changeRoom(0);
      } else {
        this.changeRoom(this.activeRoomIndex + 1);
      }
    },

    changeRoomDirectly(index) {
      this.changeRoom(index);
    },

    changeRoom(index) {
      this.activeRoomIndex = index;
      this.loadingGallery = true;
      setTimeout(() => {
        this.setActiveImages();

        nextTick(() => {
          this.loadingGallery = false;
        });
        if (this.$refs.navContainer) {
          const parentRect = this.navLinkRefs[this.activeRoomIndex].offsetParent.getBoundingClientRect();
          const offsetLeft = parentRect.left + this.navLinkRefs[this.activeRoomIndex].offsetLeft;
          this.$refs.navContainer.scrollTo({ left: offsetLeft - 130, behavior: 'smooth' });
        }
      }, 500);
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';
.shop-by {
  overflow: visible !important;
  position: relative;
  max-width: 100vw;
  min-height: 450px;
  min-width: 100%;
  white-space: nowrap;

  @media (max-width: $mobile-breakpoint) {
    padding-bottom: 0px;
  }

  @media (max-width: calc($tablet-breakpoint - 1px)) {
    min-height: 300px;
  }

  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
}

.loader-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 50%;
  left: 50%;
}

.shop-by-widget {
  color: #666666;
  width: 100%;
  height: auto;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }

  .body {
    position: relative;
    display: block;
  }

  .head {
    .shop-by-title {
      text-align: left;
      font-size: var(--font-size-24);
    }
  }
}

.shop-by-widget-nav {
  width: 100%;
  position: relative;

  &::before {
    position: absolute;
    content: '';
    bottom: 0px;
    width: 100%;
    height: 1px;
    background-color: var(--grey-ultra-light);
  }

  @media (max-width: calc($mobile-breakpoint - 1px)) {
    width: 100vw;
    margin-left: calc(50vw - 20px);
    transform: translateX(-50vw);
  }

  ::-webkit-scrollbar {
    display: none;
  }

  .shop-by-nav {
    display: flex;
    width: 100%;
    overflow: scroll;
    scrollbar-width: none;
    margin: 24px 0;
    margin-bottom: calc(var(--space-unit) * 8);
    position: relative;

    div {
      position: relative;
      font-size: var(--font-size-15);
      display: inline-flex;
      color: var(--grey-medium);
      padding: 0px;
      padding-bottom: 8px;
      min-width: fit-content;
      margin: 0 var(--space-4);
      cursor: pointer;
      transition-duration: 0.3s;
      border-bottom: 2px solid transparent;

      /* purgecss ignore */
      &:first-of-type {
        @media (max-width: calc($mobile-breakpoint - 1px)) {
          margin-left: 20px;
        }
      }

      @media (min-width: calc($mobile-breakpoint + 1px)) {
        /* purgecss ignore */
        &:hover {
          color: var(--grey-ultra-dark);
          border-color: var(--grey-medium);
          transition-duration: 0.3s;
        }
      }

      &.active {
        color: $coral;
        border-color: $coral;
        transition-duration: 0.3s;
      }

      &.placeholder {
        span {
          opacity: 0;
        }
      }
    }

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      &.shop-by-nav {
        margin-top: 0;
        margin-bottom: var(--space-4);
        div {
          padding: var(--space-4) 0;
        }
      }
    }
  }

  .product-set-list {
    margin-left: var(--space-6) !important;
  }
}
</style>
